import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import './CustomSelect.css';

// Extracts {value, label, disabled} from <option> children
function parseOptions(children) {
  return React.Children.toArray(children)
    .filter(child => child && child.type === 'option')
    .map(child => ({
      value: child.props.value !== undefined ? child.props.value : '',
      label: child.props.children,
      disabled: !!child.props.disabled,
    }));
}

const CustomSelect = ({ value, defaultValue, onChange, disabled, className, required, id, name, children }) => {
  const isControlled = value !== undefined;
  const [internalValue, setInternalValue] = useState(
    defaultValue !== undefined ? defaultValue : ''
  );
  const [open, setOpen] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState({});
  const triggerRef = useRef(null);
  const containerRef = useRef(null);

  const currentValue = isControlled ? value : internalValue;
  const options = parseOptions(children);

  const selectedOption = options.find(o => String(o.value) === String(currentValue));
  const displayLabel = selectedOption ? selectedOption.label : (options[0]?.label || '');

  const openDropdown = () => {
    if (disabled) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const maxHeight = 240;
    const spaceBelow = window.innerHeight - rect.bottom - 4;
    const spaceAbove = rect.top - 4;
    const openUpward = spaceBelow < maxHeight && spaceAbove > spaceBelow;
    setDropdownStyle(openUpward
      ? { position: 'fixed', bottom: window.innerHeight - rect.top + 4, left: rect.left, width: rect.width, zIndex: 99999 }
      : { position: 'fixed', top: rect.bottom + 4, left: rect.left, width: rect.width, zIndex: 99999 }
    );
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return;
    const handleOutside = (e) => {
      if (
        containerRef.current && !containerRef.current.contains(e.target) &&
        triggerRef.current && !triggerRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    const handleScroll = (e) => {
      if (containerRef.current && containerRef.current.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handleOutside);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [open]);

  const handleSelect = (optValue) => {
    if (!isControlled) setInternalValue(optValue);
    if (onChange) onChange({ target: { value: optValue, name } });
    setOpen(false);
  };

  const dropdown = open && ReactDOM.createPortal(
    <div
      ref={containerRef}
      className="custom-select__dropdown"
      style={dropdownStyle}
    >
      {options.map((opt, i) => (
        <div
          key={i}
          className={`custom-select__option${String(opt.value) === String(currentValue) ? ' custom-select__option--selected' : ''}${opt.disabled ? ' custom-select__option--disabled' : ''}`}
          onClick={() => !opt.disabled && handleSelect(opt.value)}
        >
          {opt.label}
        </div>
      ))}
    </div>,
    document.body
  );

  return (
    <div
      id={id}
      className={`custom-select${className ? ' ' + className : ''}${disabled ? ' custom-select--disabled' : ''}${open ? ' custom-select--open' : ''}`}
    >
      {name && (
        <input type="hidden" name={name} value={currentValue} required={required} />
      )}
      <div ref={triggerRef} className="custom-select__trigger" onClick={openDropdown}>
        <span>{displayLabel}</span>
        <svg className="custom-select__arrow" viewBox="0 0 10 6" xmlns="http://www.w3.org/2000/svg">
          <path d="M0 0l5 6 5-6z" fill="currentColor" />
        </svg>
      </div>
      {dropdown}
    </div>
  );
};

export default CustomSelect;
